import { type User } from '@pubnub/chat';
import { Badge } from '@avanta/shared/components/ui/Badge';
import { Dialog } from '@avanta/shared/components/ui/Dialog';
import { Input } from '@avanta/shared/components/ui/Input';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useConversationsStore } from '@avanta/shared/stores/chat/conversations';
import { useChatStore } from '@avanta/shared/stores/chat/index';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedSearch } from 'hooks/useDebouncedSearch';

export const NewChat = ({ onClose }: { onClose: () => void }): React.ReactElement => {
  const chat = useChatStore(state => state.chat);
  const { createConversation } = useConversationsStore();
  const isCreatingConversation = useConversationsStore(state => state.isCreatingConversation);
  const [users, setUsers] = useState<User[]>([]);
  const { t } = useTranslation(LOCIZE_NS.CHAT);

  const searchUsers = async (query: string): Promise<User[]> => {
    if (!chat) return [];
    const { users } = await chat.getUsers({
      filter: `name LIKE "*${query}*" || id LIKE "*${query}*"`,
      limit: 10,
    });
    return users;
  };

  const handleChange = async (query: string): Promise<void> => {
    if (query.length > 2) {
      const users = await searchUsers(query);
      setUsers(users);
    } else {
      setUsers([]);
    }
  };

  const debouncedSearch = useDebouncedSearch(handleChange, 300);

  const handleCreateChat = async (userId: string): Promise<void> => {
    await createConversation(userId);
    onClose();
  };

  return (
    <Dialog
      title={t('Chat.NewChat.heading')}
      description={t('Chat.NewChat.body')}
      open
      onClose={onClose}
    >
      <div className="flex flex-col gap-6">
        {isCreatingConversation ? (
          <div className="flex items-center justify-center gap-2 py-4 text-sm text-denim-600">
            <Loader2 className="animate-spin" size={20} />
            {t('Chat.NewChat.creating')}
          </div>
        ) : (
          <>
            <Input
              placeholder={t('Chat.NewChat.search.placeholder')}
              onChange={debouncedSearch}
              disabled={isCreatingConversation}
            />
            <div className="flex gap-2 flex-wrap">
              {users.map(user => (
                <Badge
                  key={user.id}
                  onClick={() => handleCreateChat(user.id)}
                  className="cursor-default bg-white text-denim-600 border border-plum-200 h-6 rounded-sm hover:bg-teal-100"
                  size="sm"
                >
                  {user.name}
                </Badge>
              ))}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};
